import React, { useState, useEffect, useRef, useCallback } from 'react';
import { documentService } from '../../services/documentService.ts';
import { TenantDocument, DocumentType } from '../../types/document.ts';
import '../../styles/DocumentUpload.css';
import { formatDate } from '../../utils/formatters.ts';
import ConfirmationModal from '../shared/ConfirmationModal.tsx';

interface DocumentUploadProps {
  tenantId: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ tenantId }) => {
  const [documents, setDocuments] = useState<TenantDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadType, setUploadType] = useState<DocumentType>('Other');
  const [documentToDelete, setDocumentToDelete] = useState<TenantDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ];

  const fetchDocuments = useCallback(async () => {
    try {
      const data = await documentService.getDocuments(tenantId);
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      alert('Invalid file type. Only PDF and image files (PNG, JPG) are allowed');
      e.target.value = ''; // Reset input
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      alert('File size exceeds 5MB limit');
      e.target.value = ''; // Reset input
      return;
    }

    setLoading(true);
    try {
      await documentService.uploadDocument(file, tenantId, uploadType);
      await fetchDocuments();
      e.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const initiateDelete = (doc: TenantDocument) => {
    setDocumentToDelete(doc);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;
    
    try {
      await documentService.deleteDocument(documentToDelete.id);
      setDocuments(documents.filter(doc => doc.id !== documentToDelete.id));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    } finally {
      setDocumentToDelete(null);
    }
  };

  const downloadDocument = async (doc: TenantDocument) => {
    try {
      const url = await documentService.getOrRefreshUrl(doc.file_path);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('No URL returned');
      }
    } catch (error) {
      console.error('Error getting document URL:', error);
      alert('Failed to load document');
    }
  };

  return (
    <div className="document-upload">
      <div className="upload-controls">
        <select
          value={uploadType}
          onChange={(e) => setUploadType(e.target.value as DocumentType)}
        >
          <option value="Aadhar_Card">Aadhar Card</option>
          <option value="PAN_Card">PAN Card</option>
          <option value="Rental_Agreement">Rental Agreement</option>
          <option value="Police_Verification">Police Verification</option>
          <option value="Other">Other</option>
        </select>
        
        <div className="file-input-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            disabled={loading}
            accept=".pdf,.jpg,.jpeg,.png"
            className="file-input"
            id="file-upload"
          />
          <label 
            htmlFor="file-upload" 
            className={`file-input-label ${loading ? 'loading' : ''}`}
          >
            <i className="fas fa-cloud-upload-alt"></i>
            {loading ? 'Uploading...' : 'Upload Document'}
          </label>
        </div>
          <span className="form-hint">File types: PDF, JPG, PNG / Maximum file size: 5MB</span>
      </div>

      <div className="documents-list">
        {documents.map((doc) => (
          <div key={doc.id} className="document-item">
            <div className="document-info">
              <span className="document-type">{doc.document_type}</span>
              <span className="document-name">{doc.file_name}</span>
              <span className="document-date">
                {formatDate(doc.uploaded_at)}
              </span>
            </div>
            <div className="document-actions">
              <button 
                onClick={() => downloadDocument(doc)} 
                className="btn-view"
              >
                View
              </button>
              <button
                onClick={() => initiateDelete(doc)}
                className="btn-delete"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {documents.length === 0 && (
          <p className="no-documents">No documents uploaded yet.</p>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!documentToDelete}
        message={`Are you sure you want to delete "${documentToDelete?.file_name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setDocumentToDelete(null)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default DocumentUpload;
